import React, { useRef, useState } from 'react';
import './EqxFilter.css';

export interface EqxFilterProps<T> {
    data: T[];
    onFilterChange: (filteredData: T[]) => void;
    placeholder?: string;
    searchKeys?: (keyof T)[];
    className?: string;
    debounceMs?: number;
}

function EqxFilter<T extends Record<string, any>>({
    data,
    onFilterChange,
    placeholder = "Search...",
    searchKeys,
    className = '',
    debounceMs
}: EqxFilterProps<T>) {
    const [searchTerm, setSearchTerm] = useState('');
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Filter function
    const filterData = (term: string) => {
        if (!term.trim()) {
            onFilterChange(data);
            return;
        }

        const filtered = data.filter(item => {
            if (searchKeys && searchKeys.length > 0) {
                // Search across specified keys
                return searchKeys.some(key => {
                    const value = item[key];
                    return value && value.toString().toLowerCase().includes(term.toLowerCase());
                });
            } else {
                // Search across all string/number values
                return Object.values(item).some(value => {
                    if (value && typeof value === 'string' || typeof value === 'number') {
                        return value.toString().toLowerCase().includes(term.toLowerCase());
                    }
                    return false;
                });
            }
        });

        onFilterChange(filtered);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (debounceMs) {
            // Clear previous timeout and set new one
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                filterData(value);
            }, debounceMs);
        } else {
            // No debouncing - filter immediately
            filterData(value);
        }
    };

    const handleClear = () => {
        setSearchTerm('');
        onFilterChange(data);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    return (
        <div className={`eqx-filter ${className}`}>
            <div className="input-group">
                <span className="input-group-text">
                    <i className="fas fa-search"></i>
                </span>
                <input
                    type="text"
                    className="form-control"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                {searchTerm && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleClear}
                        title="Clear search"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                )}
            </div>
        </div>
    );
}

export default EqxFilter; 